import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api } from '../services/api';
import { User } from '../types';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  requiresPasswordReset: boolean;
  isAdmin: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    if (api.isAuthenticated()) {
      api.getCurrentUser()
        .then((currentUser) => {
          setUser(currentUser);
          setRequiresPasswordReset(currentUser.forcePasswordReset);
        })
        .catch(() => {
          api.logout();
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (username: string, password: string) => {
    const response = await api.login({ username, password });
    const currentUser = await api.getCurrentUser();
    setUser(currentUser);
    setRequiresPasswordReset(response.forcePasswordReset);
  };

  const logout = () => {
    api.logout();
    setUser(null);
    setRequiresPasswordReset(false);
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    await api.changePassword({ currentPassword, newPassword });
    // After successful password change, clear the flag
    setRequiresPasswordReset(false);
    if (user) {
      setUser({ ...user, forcePasswordReset: false });
    }
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading,
      requiresPasswordReset,
      isAdmin: user?.role === 'Admin',
      login,
      logout,
      changePassword,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
